// Phase 3 - Legal description generator.
// 1) Fetch PLSS aliquot cells from BLM (via our proxy).
// 2) Decide if the parcel is "aliquot aligned": i.e. parcel area is essentially
//    equal to the union of some PLSSSecondDivision cells whose overlap with the
//    parcel is either ~100% or ~0%.
//    - If yes: format as aliquot notation (e.g. "The SW 1/4 of the NE 1/4 of
//      Section 18, Township 6 North, Range 52 West, 6th Meridian").
//    - If no: fall back to metes-and-bounds (bearings + distances between vertices).
//
// Important: BLM CadNSDI uses SECDIVNO as the aliquot code (e.g. "SWNE" means
// "SW 1/4 of NE 1/4"). QSEC/QQSEC are often null. We pull from SECDIVNO first.

import { area, intersect, bearing, distance } from '@turf/turf';
import { fetchPlssAliquotsForParcel } from './plss.js';

export async function generateLegalDescription(parcelFeature) {
  if (!parcelFeature || !parcelFeature.geometry) {
    return { placeholder: true, kind: 'none', text: 'No parcel selected.' };
  }
  try {
    const aliquots = await fetchPlssAliquotsForParcel(parcelFeature);
    const alignment = checkAliquotAlignment(parcelFeature, aliquots);
    if (alignment.aligned && alignment.cells.length > 0) {
      return { placeholder: false, kind: 'aliquot', text: formatAliquot(alignment.cells) };
    }
  } catch (err) {
    console.warn('PLSS alignment check failed, falling back to metes-and-bounds:', err);
  }
  return { placeholder: false, kind: 'metes', text: formatMetesAndBounds(parcelFeature) };
}

// ---- Aliquot alignment ----
// Tolerances:
//   A cell with >= CELL_IN threshold of its area inside the parcel counts as "in".
//   A cell with <= CELL_OUT threshold of its area inside counts as "out".
//   Cells in between => alignment is ambiguous => not aliquot-aligned.
// The total area of "in" cells must be within TOTAL_TOL of the parcel's area.
function checkAliquotAlignment(parcel, aliquotFC) {
  // Classify each cell by majority overlap:
  //   >=50% inside parcel => count as "contained"
  //   <50% inside         => count as "outside"
  // Then check if the total area of contained cells matches the parcel area
  // within TOTAL_TOL. This is tolerant of small dataset-drift overlaps at
  // the parcel boundary (e.g. 5-15% sliver overlaps) which would otherwise
  // trigger false "not aligned" bails.
  const CELL_IN = 0.5;
  const TOTAL_TOL = 0.12; // allow 12% area drift

  const parcelArea = safeArea(parcel);
  if (!parcelArea) return { aligned: false };

  const contained = [];
  let totalContainedArea = 0;

  for (const cell of aliquotFC.features || []) {
    const cellArea = safeArea(cell);
    if (!cellArea) continue;
    const inter = safeIntersect(parcel, cell);
    const interArea = inter ? safeArea(inter) : 0;
    const frac = interArea / cellArea;
    if (frac >= CELL_IN) {
      contained.push(cell);
      totalContainedArea += cellArea;
    }
  }
  if (contained.length === 0) return { aligned: false };
  const ratio = totalContainedArea / parcelArea;
  if (ratio >= 1 - TOTAL_TOL && ratio <= 1 + TOTAL_TOL) {
    return { aligned: true, cells: contained };
  }
  return { aligned: false };
}

function safeArea(f) { try { return area(f); } catch { return 0; } }
function safeIntersect(a, b) {
  try { return intersect({ type: 'FeatureCollection', features: [a, b] }); }
  catch { try { return intersect(a, b); } catch { return null; } }
}

// ---- Aliquot text formatting ----
function formatAliquot(cells) {
  // Group by section so each section gets one sentence.
  const groups = new Map();
  for (const cell of cells) {
    const a = cell.properties || {};
    const secKey = [
      pickFirst(a, ['TWNSHPNO','TWNSHP','TOWNSHIP']),
      pickFirst(a, ['TWNSHPDIR','TOWNSHIPDIR']),
      pickFirst(a, ['RANGENO','RANGE']),
      pickFirst(a, ['RANGEDIR']),
      pickFirst(a, ['FRSTDIVNO','SECTIONNO','SECNO','SECTION']),
      pickFirst(a, ['PRINMER','PRINMERCD','MERIDIAN']),
    ].join('|');
    if (!groups.has(secKey)) groups.set(secKey, []);
    groups.get(secKey).push(cell);
  }

  const sentences = [];
  for (const [, grp] of groups) {
    const first = grp[0].properties || {};
    const section = pickFirst(first, ['FRSTDIVNO','SECTIONNO','SECNO','SECTION']);
    const twn = pickFirst(first, ['TWNSHPNO','TWNSHP','TOWNSHIP']);
    const twnDir = pickFirst(first, ['TWNSHPDIR','TOWNSHIPDIR']);
    const rng = pickFirst(first, ['RANGENO','RANGE']);
    const rngDir = pickFirst(first, ['RANGEDIR']);
    const pm = pickFirst(first, ['PRINMER','PRINMERCD','MERIDIAN']);

    // If all 16 QQ cells of this section are contained -> "all of Section X"
    // Otherwise, list the contained aliquots.
    const labels = grp.map((c) => aliquotLabel(c.properties || {})).filter(Boolean);
    const uniqueLabels = Array.from(new Set(labels));
    let aliquotPhrase;
    if (grp.length >= 16) {
      aliquotPhrase = 'All of';
    } else if (uniqueLabels.length === 1) {
      aliquotPhrase = 'The ' + uniqueLabels[0] + ' of';
    } else {
      aliquotPhrase = 'The ' + uniqueLabels.slice(0, -1).join(', ')
                    + ' and ' + uniqueLabels[uniqueLabels.length - 1] + ' of';
    }

    const secText = section ? `Section ${stripLeadingZeros(section)}` : 'an unknown Section';
    const twnText = twn ? `Township ${stripLeadingZeros(twn)}${twnDir ? ' ' + dirFull(twnDir) : ''}` : '';
    const rngText = rng ? `Range ${stripLeadingZeros(rng)}${rngDir ? ' ' + dirFull(rngDir) : ''}` : '';
    const pmText = pm ? (String(pm).match(/meridian/i) ? String(pm) : `${pm} Principal Meridian`) : '';

    const loc = [secText, twnText, rngText, pmText].filter(Boolean).join(', ');
    sentences.push(`${aliquotPhrase} ${loc}.`);
  }
  return sentences.join('\n');
}

function aliquotLabel(attrs) {
  // BLM CadNSDI uses SECDIVNO with codes like "NE", "SWNE" (= SW 1/4 of NE 1/4).
  const raw = pickFirst(attrs, ['SECDIVNO', 'SECDIVLAB', 'QQSEC', 'QSEC']);
  if (!raw) return '';
  const code = String(raw).toUpperCase().replace(/[^A-Z]/g, '');
  if (code.length === 2) return `${quarterName(code)} 1/4`;
  if (code.length === 4) return `${quarterName(code.slice(0, 2))} 1/4 of the ${quarterName(code.slice(2, 4))} 1/4`;
  if (code.length === 6) return `${quarterName(code.slice(0, 2))} 1/4 of the ${quarterName(code.slice(2, 4))} 1/4 of the ${quarterName(code.slice(4, 6))} 1/4`;
  return code;
}

function quarterName(code) {
  return { NE:'Northeast', NW:'Northwest', SE:'Southeast', SW:'Southwest' }[code] || code;
}

function dirFull(d) {
  return { N:'North', S:'South', E:'East', W:'West' }[String(d).toUpperCase()] || d;
}

function stripLeadingZeros(s) {
  const t = String(s).replace(/^0+/, '');
  return t === '' ? '0' : t;
}

function pickFirst(obj, keys) {
  for (const k of keys) if (obj[k] != null && obj[k] !== '') return obj[k];
  return null;
}

// ---- Metes & bounds formatting ----
function formatMetesAndBounds(parcel) {
  const ring = outerRingVertices(parcel);
  if (ring.length < 3) return 'Unable to extract polygon vertices.';
  const lines = [];
  lines.push('Beginning at the point of beginning at approximately ' + fmtCoord(ring[0]) + ';');
  for (let i = 0; i < ring.length; i++) {
    const from = ring[i];
    const to = ring[(i + 1) % ring.length];
    const dFeet = distance(from, to, { units: 'feet' });
    const br = bearing(from, to);
    const isLast = i === ring.length - 1;
    lines.push(`thence ${bearingToQuadrant(br)}, ${dFeet.toFixed(2)} feet${isLast ? ' to the point of beginning.' : ';'}`);
  }
  return lines.join('\n');
}

export function outerRingVertices(feature) {
  const g = feature && feature.geometry;
  if (!g) return [];
  if (g.type === 'Polygon') return g.coordinates[0].slice(0, -1);
  if (g.type === 'MultiPolygon') {
    let best = [];
    for (const poly of g.coordinates) {
      const r = poly[0];
      if (r.length > best.length) best = r.slice(0, -1);
    }
    return best;
  }
  return [];
}

function bearingToQuadrant(deg) {
  let b = ((deg + 360) % 360);
  let ns, ew, angle;
  if (b <= 90)       { ns='N'; ew='E'; angle = b; }
  else if (b <= 180) { ns='S'; ew='E'; angle = 180 - b; }
  else if (b <= 270) { ns='S'; ew='W'; angle = b - 180; }
  else               { ns='N'; ew='W'; angle = 360 - b; }
  const d = Math.floor(angle);
  const mF = (angle - d) * 60;
  const m = Math.floor(mF);
  const s = Math.round((mF - m) * 60);
  return `${ns} ${d}\u00b0 ${String(m).padStart(2,'0')}' ${String(s).padStart(2,'0')}" ${ew}`;
}

function fmtCoord(c) {
  return `(${c[1].toFixed(6)}, ${c[0].toFixed(6)})`;
}
